import { useSuspenseQuery } from '@apollo/client';
import { useMemo } from 'react';

import { MedicalNotesLlmOutputTypes } from '@eluve/graphql-types';
import { isLlmOutputSchedulable } from '@eluve/llm-outputs';
import { formatHumanName } from '@eluve/utils';

import {
  getTenantPromptTemplateDefaultsQuery,
  getTenantUsersForOverridesQuery,
} from '../promptTemplateDefaults.operations';

const TENANT_MANAGED_OUTPUT_TYPES = new Set<string>(MedicalNotesLlmOutputTypes);

export const useTenantPromptDefaultsData = (tenantId: string) => {
  const { data: defaultsData, refetch: refetchDefaults } = useSuspenseQuery(
    getTenantPromptTemplateDefaultsQuery,
    {
      variables: { tenantId },
    },
  );

  const { data: usersData } = useSuspenseQuery(
    getTenantUsersForOverridesQuery,
    {
      variables: { tenantId },
    },
  );

  const tenantDefaults = defaultsData?.tenantDefaults ?? [];
  const userDefaults = defaultsData?.userDefaults ?? [];
  const tenantName = usersData?.tenantsByPk?.name ?? 'Organization';

  // Use tenant-enabled LLM output types (filter global list by what's enabled for tenant)
  const llmOutputTypes = useMemo(() => {
    const enabledOutputNames = new Set<string>(
      defaultsData?.tenantsByPk?.tenant_llm_outputs?.map(
        (o) => o.llmOutputName,
      ) ?? [],
    );
    // Include:
    // 1. System-level types (non-schedulable) - always visible
    // 2. Tenant-enabled types - filtered by tenant_llm_outputs
    return (defaultsData?.llmOutputTypes ?? []).filter((ot) => {
      const outputName = ot.name as string;
      const isSystemLevel = !isLlmOutputSchedulable(outputName);
      const isTenantEnabled = enabledOutputNames.has(outputName);
      return isSystemLevel || isTenantEnabled;
    });
  }, [
    defaultsData?.llmOutputTypes,
    defaultsData?.tenantsByPk?.tenant_llm_outputs,
  ]);

  // Use tenant-enabled prompt templates + global templates for system-level output types
  const promptTemplates = useMemo(() => {
    // Tenant-assigned templates (only for SOAP_NOTE and DYNAMIC_OUTPUT)
    const tenantTemplates = defaultsData?.tenantsByPk?.promptTemplates ?? [];
    const tenantEnabledTemplates = tenantTemplates
      .filter(
        (t) =>
          t.isEnabled &&
          TENANT_MANAGED_OUTPUT_TYPES.has(t.prompt_template.outputType),
      )
      .map((t) => t.prompt_template);

    // Global templates for all other output types
    const globalTemplates = defaultsData?.promptTemplates ?? [];
    const systemLevelTemplates = globalTemplates.filter(
      (pt) => !TENANT_MANAGED_OUTPUT_TYPES.has(pt.outputType),
    );

    // Combine both sets (tenant templates + system-level templates)
    // Use a Map to dedupe by template ID in case of overlap
    const templateMap = new Map<string, (typeof globalTemplates)[0]>();
    for (const template of tenantEnabledTemplates) {
      templateMap.set(template.id, template);
    }
    for (const template of systemLevelTemplates) {
      templateMap.set(template.id, template);
    }

    return Array.from(templateMap.values());
  }, [
    defaultsData?.tenantsByPk?.promptTemplates,
    defaultsData?.promptTemplates,
  ]);

  // Extract tenant-enabled output templates
  const outputTemplates = useMemo(() => {
    const tenantOutputTemplates =
      defaultsData?.tenantsByPk?.llmOutputTemplates ?? [];
    return tenantOutputTemplates.map((t) => ({
      id: t.llm_output_template.id,
      name: t.llm_output_template.name,
      llmOutputType: t.llm_output_template.llmOutputType as string,
    }));
  }, [defaultsData?.tenantsByPk?.llmOutputTemplates]);

  // Build user list with override counts from aggregate
  const usersWithCounts = useMemo(
    () =>
      (usersData?.tenantsByPk?.tenantUsers ?? []).map((tu) => {
        const user = tu.user as {
          email: string;
          firstName: string | null;
          lastName: string | null;
          promptTemplateDefaultsAggregate?: {
            aggregate?: { count: number };
          };
        };

        return {
          userId: tu.userId,
          name: formatHumanName(user.firstName, user.lastName) || user.email,
          email: user.email,
          overrideCount:
            user.promptTemplateDefaultsAggregate?.aggregate?.count ?? 0,
        };
      }),
    [usersData?.tenantsByPk?.tenantUsers],
  );

  return {
    tenantDefaults,
    userDefaults,
    tenantName,
    llmOutputTypes,
    promptTemplates,
    outputTemplates,
    usersWithCounts,
    refetchDefaults,
  };
};
